import React from 'react';
import { Controller } from 'react-hook-form';
import HookFormUtils from './HookFormUtils';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { Box, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

export default function HookFormDateTimePicker({
  control,
  schema,
  variant,
  type = 'text',
  loading,
  errors,
  label,
  prefix = null,
  ...others
}) {
  const error = HookFormUtils.getFieldError(errors, schema, prefix);
  const _schema = HookFormUtils.getSchemaName(schema, prefix);

  return (
    <Controller
      control={control}
      name={_schema}
      render={({ field: { onChange, value } }) => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={3}>
            <Box>
              {/* Optional title styling to mimic the modal header; keep it lightweight so parent can still control titles */}
              {label && (
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {label}
                </Typography>
              )}

              {/* DATE */}
              <DatePicker
                disablePast={others?.disablePast}
                value={value ? dayjs(value) : null}
                onChange={(newDate) => {
                  if (!newDate || !newDate.isValid()) {
                    onChange(null);
                    return;
                  }
                  const current = value ? dayjs(value) : dayjs();
                  const updated = newDate
                    .hour(current.hour() || 9)
                    .minute(Math.floor((current.minute() || 0) / 15) * 15)
                    .second(0)
                    .millisecond(0);
                  onChange(updated);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    variant={variant || 'outlined'}
                    error={!!error}
                    helperText={error && error.message}
                  />
                )}
                {...others}
              />

              {/* TIME */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Time
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {/* Hour select */}
                  <FormControl fullWidth>
                    <InputLabel id={`${_schema}-hour-label`}>Hour</InputLabel>
                    <Select
                      labelId={`${_schema}-hour-label`}
                      label="Hour"
                      value={(() => {
                        const d = value ? dayjs(value) : null;
                        const h = d && d.isValid() ? d.hour() : 9;
                        return String(h).padStart(2, '0');
                      })()}
                      onChange={(e) => {
                        const h = parseInt(e.target.value, 10);
                        const d = value ? dayjs(value) : dayjs();
                        const m = d.minute();
                        const snappedM = Math.floor((m || 0) / 15) * 15;
                        const updated = (d.isValid() ? d : dayjs())
                          .hour(h)
                          .minute(snappedM)
                          .second(0)
                          .millisecond(0);
                        onChange(updated);
                      }}
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <MenuItem key={i} value={String(i).padStart(2, '0')}>
                          {String(i).padStart(2, '0')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Typography variant="h6" component="span" sx={{ lineHeight: 1 }}>
                    :
                  </Typography>

                  {/* Minute select */}
                  <FormControl fullWidth>
                    <InputLabel id={`${_schema}-minute-label`}>Minute</InputLabel>
                    <Select
                      labelId={`${_schema}-minute-label`}
                      label="Minute"
                      value={(() => {
                        const d = value ? dayjs(value) : null;
                        const m = d && d.isValid() ? d.minute() : 0;
                        const snapped = Math.floor((m || 0) / 15) * 15;
                        return String(snapped).padStart(2, '0');
                      })()}
                      onChange={(e) => {
                        const m = parseInt(e.target.value, 10);
                        const d = value ? dayjs(value) : dayjs();
                        const updated = (d.isValid() ? d : dayjs())
                          .minute(m)
                          .second(0)
                          .millisecond(0);
                        onChange(updated);
                      }}
                    >
                      {[0, 15, 30, 45].map((mm) => (
                        <MenuItem key={mm} value={String(mm).padStart(2, '0')}>
                          {String(mm).padStart(2, '0')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                  Time slots available every 15 minutes
                </Typography>
              </Box>
            </Box>
          </Stack>
        </LocalizationProvider>
      )}
    />
  );
}